import axios, {
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from "axios";
import {
  accessTokenRequestInterceptor,
  onSiteProxyRequestInterceptor,
} from "config/axios-interceptors";
import {
  dsmConcurrencyLimiter,
  isConcurrencyLimitedUrl,
} from "service/request-concurrency-limiter";
import { STORAGE_KEYS } from "service/storage";
import { hasUserInteracted } from "service/user-activity.service";

export interface AxiosInstanceConfig {
  getAccessToken?: () => Promise<string | undefined>;
  bearerToken?: string;
}

interface InterceptorManager {
  instance: AxiosInstance;
  requestInterceptorIds: number[];
  responseInterceptorIds: number[];
  config: AxiosInstanceConfig;
}

/**
 * Service for managing axios instances with proper interceptor lifecycle
 * Provides both dedicated and shared axios instances
 */
class AxiosInstanceService {
  private sharedInstance: InterceptorManager | null = null;
  private sharedConfigKey: string | null = null;

  /**
   * Create a new axios instance with configured interceptors
   */
  createInstance(config: AxiosInstanceConfig): AxiosInstance {
    const axiosInstance = axios.create();
    this.setupInterceptors(axiosInstance, config);
    return axiosInstance;
  }

  /**
   * Get or create a shared axios instance
   * Multiple calls with the same config will return the same instance
   */
  getSharedInstance(config: AxiosInstanceConfig): AxiosInstance {
    const configKey = this.getConfigKey(config);

    // If config has changed, reset shared instance
    if (this.sharedConfigKey !== configKey) {
      this.resetSharedInstance();
      this.sharedConfigKey = configKey;
    }

    // Create shared instance if it doesn't exist
    if (!this.sharedInstance) {
      const instance = axios.create();
      const { requestInterceptorIds, responseInterceptorIds } =
        this.setupInterceptors(instance, config);
      this.sharedInstance = {
        instance,
        requestInterceptorIds,
        responseInterceptorIds,
        config,
      };
    }

    return this.sharedInstance.instance;
  }

  /**
   * Reset the shared axios instance (useful for testing or logout)
   */
  resetSharedInstance(): void {
    if (this.sharedInstance) {
      for (const id of this.sharedInstance.requestInterceptorIds) {
        this.sharedInstance.instance.interceptors.request.eject(id);
      }
      for (const id of this.sharedInstance.responseInterceptorIds) {
        this.sharedInstance.instance.interceptors.response.eject(id);
      }
    }
    this.sharedInstance = null;
    this.sharedConfigKey = null;
  }

  /**
   * Setup interceptors for an axios instance
   * Returns interceptor IDs for cleanup
   */
  private setupInterceptors(
    instance: AxiosInstance,
    config: AxiosInstanceConfig,
  ): { requestInterceptorIds: number[]; responseInterceptorIds: number[] } {
    const requestInterceptorIds: number[] = [];
    const responseInterceptorIds: number[] = [];

    type RetryableConfig = InternalAxiosRequestConfig & {
      _authRetried?: boolean;
      _userInteractedAtStart?: boolean;
    };

    // Add authentication interceptor
    if (config.bearerToken) {
      // Static token-based authentication
      const tokenInterceptorId = instance.interceptors.request.use(
        (requestConfig: InternalAxiosRequestConfig) => {
          requestConfig.headers.Authorization = `Bearer ${config.bearerToken}`;
          return requestConfig;
        },
        (error) => Promise.reject(error),
      );
      requestInterceptorIds.push(tokenInterceptorId);
    } else if (config.getAccessToken) {
      // Dynamic token-based authentication
      const dynamicTokenInterceptorId = instance.interceptors.request.use(
        accessTokenRequestInterceptor(config.getAccessToken),
      );
      requestInterceptorIds.push(dynamicTokenInterceptorId);
    }

    // Add proxy interceptor (always active)
    const proxyInterceptorId = instance.interceptors.request.use(
      onSiteProxyRequestInterceptor,
    );
    requestInterceptorIds.push(proxyInterceptorId);

    // CX-3744: snapshot the interaction state when the request is *created*, not
    // when a later 401 resolves. hasUserInteracted() latches on the first
    // pointer/keydown and only resets on navigation; reading it at resolve time
    // lets an incidental tap during the async refresh window (up to
    // CDC_JWT_TIMEOUT_MS) flip a background bootstrap 401 into a dialog — the
    // exact bug this fix targets. A request that started before any interaction
    // is background detection regardless of what the user does while it is in
    // flight.
    const interactionSnapshotId = instance.interceptors.request.use(
      (requestConfig) => {
        (requestConfig as RetryableConfig)._userInteractedAtStart =
          hasUserInteracted();
        return requestConfig;
      },
    );
    requestInterceptorIds.push(interactionSnapshotId);

    // CX-3645: gate DSM proxy requests through a shared concurrency limiter so a
    // grid of ~40 tiles can't fire ~40 parallel GETs at the slow DSM upstream and
    // trip the 15s Lambda timeout (which surfaces as a 502). Scoped to the DSM URL
    // prefix; honors the request's AbortSignal so a queued waiter that is cancelled
    // never consumes a slot.
    type ConcurrencyGatedConfig = InternalAxiosRequestConfig & {
      _dsmSlotHeld?: boolean;
    };

    const releaseDsmSlot = (requestConfig?: ConcurrencyGatedConfig) => {
      if (requestConfig?._dsmSlotHeld) {
        requestConfig._dsmSlotHeld = false;
        dsmConcurrencyLimiter.release();
      }
    };

    const dsmGateInterceptorId = instance.interceptors.request.use(
      async (requestConfig: ConcurrencyGatedConfig) => {
        if (isConcurrencyLimitedUrl(requestConfig.url)) {
          try {
            await dsmConcurrencyLimiter.acquire(
              requestConfig.signal as AbortSignal | undefined,
            );
          } catch {
            throw new axios.CanceledError();
          }
          requestConfig._dsmSlotHeld = true;
        }
        return requestConfig;
      },
    );
    requestInterceptorIds.push(dsmGateInterceptorId);

    // Release the slot as soon as the response settles. Registered BEFORE the 401
    // interceptor so a token-refresh retry re-acquires a fresh slot instead of
    // deadlocking behind the one it still holds.
    const dsmReleaseInterceptorId = instance.interceptors.response.use(
      (response) => {
        releaseDsmSlot(response.config as ConcurrencyGatedConfig);
        return response;
      },
      (error) => {
        releaseDsmSlot(error?.config as ConcurrencyGatedConfig | undefined);
        return Promise.reject(error);
      },
    );
    responseInterceptorIds.push(dsmReleaseInterceptorId);

    // Add 401 response interceptor for automatic token refresh and retry
    const responseInterceptorId = instance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config as RetryableConfig | undefined;
        if (
          error.response?.status !== 401 ||
          !originalRequest ||
          originalRequest._authRetried
        ) {
          return Promise.reject(error);
        }

        originalRequest._authRetried = true;

        const cdcUser = localStorage.getItem(STORAGE_KEYS.CDC_USER);
        let newToken: string | undefined;

        if (cdcUser) {
          const { CdcTokenService } = await import("service/cdc-token.service");
          if (CdcTokenService.isGigyaAvailable()) {
            newToken = await CdcTokenService.refreshToken("retry-401");
          }
        }

        if (!newToken && config.getAccessToken) {
          newToken = await config.getAccessToken();
        }

        if (newToken) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return instance.request(originalRequest);
        }

        // A request failed auth. Only prompt re-login when this request was
        // issued after the visitor had interacted with the page — a 401 on a
        // request the user triggered is active use and should surface the dialog
        // (CX-1948). The many bootstrap requests that fire on mount for a
        // returning visitor with an expired stored token were issued with no
        // prior interaction, so they clear state silently instead of popping the
        // dialog on the landing page (CX-3744). The flag is snapshotted at
        // request-start (see the request interceptor above), not read here, so a
        // tap during the refresh window can't flip a background 401 into a pop.
        window.dispatchEvent(
          new CustomEvent("auth:session-expired", {
            detail: {
              showDialog: originalRequest._userInteractedAtStart === true,
            },
          }),
        );
        return Promise.reject(error);
      },
    );
    responseInterceptorIds.push(responseInterceptorId);

    // CX-3645: last-resort graceful retry so a single transient DSM 502/503/504 or
    // gateway timeout doesn't surface to the user as a hard error. Bounded to one
    // retry, DSM GETs only, and registered AFTER the 401 handler so auth refresh
    // takes precedence.
    type TransientRetryConfig = InternalAxiosRequestConfig & {
      _dsmTransientRetried?: boolean;
    };
    const TRANSIENT_STATUSES = new Set([502, 503, 504]);
    const isTransientFailure = (error: {
      code?: string;
      response?: { status?: number };
    }): boolean =>
      error.code === "ECONNABORTED" ||
      error.code === "ETIMEDOUT" ||
      TRANSIENT_STATUSES.has(error.response?.status ?? 0);

    const transientRetryInterceptorId = instance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const requestConfig = error?.config as TransientRetryConfig | undefined;
        if (
          !requestConfig ||
          requestConfig._dsmTransientRetried ||
          (requestConfig.method ?? "get").toLowerCase() !== "get" ||
          !isConcurrencyLimitedUrl(requestConfig.url) ||
          !isTransientFailure(error)
        ) {
          return Promise.reject(error);
        }
        requestConfig._dsmTransientRetried = true;
        await new Promise((resolve) => setTimeout(resolve, 400));
        return instance.request(requestConfig);
      },
    );
    responseInterceptorIds.push(transientRetryInterceptorId);

    return { requestInterceptorIds, responseInterceptorIds };
  }

  /**
   * Generate a config key for caching
   */
  private getConfigKey(config: AxiosInstanceConfig): string {
    return JSON.stringify({
      hasBearerToken: !!config.bearerToken,
      hasGetAccessToken: !!config.getAccessToken,
      bearerTokenValue: config.bearerToken,
    });
  }
}

// Export singleton instance
export const axiosInstanceService = new AxiosInstanceService();

/**
 * Create a new axios instance with configured interceptors
 * Each call creates a new instance
 */
export function createAxiosInstance(
  config: AxiosInstanceConfig = {},
): AxiosInstance {
  return axiosInstanceService.createInstance(config);
}

/**
 * Get a shared axios instance
 * Multiple calls with the same config will return the same instance
 * This is useful for components that all need the same axios configuration
 */
export function getSharedAxiosInstance(
  config: AxiosInstanceConfig = {},
): AxiosInstance {
  return axiosInstanceService.getSharedInstance(config);
}

/**
 * Reset the shared axios instance
 * Useful for testing or when user logs out
 */
export function resetSharedAxiosInstance(): void {
  axiosInstanceService.resetSharedInstance();
}
